if (!customElements.get('bundle-save')) {
  customElements.define(
    'bundle-save',
    class BundleSave extends HTMLElement {
      connectedCallback() {
        this.listEl = this.querySelector('[data-bundle-save-list]');
        this.errorEl = this.querySelector('[data-bundle-save-error]');
        this.discountPercent = Number(this.dataset.discountPercent || 0);
        this.offerBadge = this.dataset.offerBadge || `Save ${this.discountPercent}%`;
        this.offerBadgeIdle = this.dataset.offerBadgeIdle || `Save up to ${this.discountPercent}%`;
        this.colorLabel = this.dataset.colorLabel || 'Select Color';
        this.products = this.readJson('[data-bundle-save-products]') || [];
        this.hostVariants = this.readJson('[data-bundle-save-host-variants]') || [];
        this.form = document.getElementById(this.dataset.formId);

        this.bindCard();
        this.patchVariantAtcUpdates();
        this.cacheAtcOriginals();

        if (this.form) {
          const idInput = this.form.querySelector('[name="id"]');
          if (idInput) idInput.addEventListener('change', () => this.scheduleAtcUpdate());
        }
        this.renderPrices();
        this.scheduleAtcUpdate();
      }

      patchVariantAtcUpdates() {
        const tryPatch = () => {
          if (window.__bundleSaveAtcPatched) return true;
          const Ctor = customElements.get('variant-selects');
          if (!Ctor?.prototype?.toggleAddButton) return false;

          window.__bundleSaveAtcPatched = true;
          const original = Ctor.prototype.toggleAddButton;
          Ctor.prototype.toggleAddButton = function patchedToggleAddButton(...args) {
            const result = original.apply(this, args);
            document.querySelectorAll('bundle-save').forEach((el) => {
              if (typeof el.scheduleAtcUpdate === 'function') el.scheduleAtcUpdate();
            });
            return result;
          };
          return true;
        };

        if (tryPatch()) return;
        if (window.customElements?.whenDefined) {
          customElements.whenDefined('variant-selects').then(tryPatch);
        }
      }

      bindCard() {
        this.card = this.querySelector('.bundle-save__card');
        this.toggle = this.querySelector('[data-bundle-save-toggle]');
        this.productSelect = this.querySelector('[data-bundle-save-product]');
        this.colorSelect = this.querySelector('[data-bundle-save-color]');
        this.badgeEl = this.querySelector('[data-bundle-save-badge]');
        this.priceEl = this.querySelector('[data-bundle-save-price]');
        this.compareEl = this.querySelector('[data-bundle-save-compare]');

        if (!this.toggle) return;

        this.toggle.addEventListener('change', () => this.sync());
        this.productSelect.addEventListener('change', () => {
          this.populateColors();
          this.sync();
        });
        this.colorSelect.addEventListener('change', () => this.sync());
      }

      readJson(selector) {
        const script = this.querySelector(selector);
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return null;
        }
      }

      colorLabelFor(product, variant) {
        const optionIndex = Number(product.colorOptionIndex || 0);
        return (variant.options && variant.options[optionIndex]) || variant.title;
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      startingPrice() {
        if (this.dataset.startingPrice) return Number(this.dataset.startingPrice);
        return Number(this.products[0]?.variants?.[0]?.price || 0);
      }

      showPlaceholderPrices() {
        this.priceEl.textContent = '$XX.XX';
        this.compareEl.textContent = this.discountPercent ? '$XX.XX' : '';
      }

      renderPrices() {
        const variant = this.selectedVariant();
        if (!this.toggle?.checked || !variant) {
          this.showPlaceholderPrices();
          return;
        }

        const compareCents = Number(variant.price);
        this.priceEl.textContent = this.formatMoney(this.discountedPrice(compareCents));
        this.compareEl.textContent = this.discountPercent ? this.formatMoney(compareCents) : '';
      }

      cacheAtcOriginals() {
        if (!this.form) return;
        this.atcOriginals = this.atcOriginals || new Map();
        this.form.querySelectorAll('.product-form__submit.btn-temp, .product-form__submit[name="add"]').forEach((button) => {
          if (this.atcOriginals.has(button)) return;
          const span = button.querySelector('span');
          if (span) this.atcOriginals.set(button, span.innerHTML);
        });
      }

      selectedProduct() {
        return this.products.find((product) => String(product.id) === this.productSelect.value) || null;
      }

      selectedVariant() {
        const product = this.selectedProduct();
        if (!product) return null;
        return product.variants.find((variant) => String(variant.id) === this.colorSelect.value) || null;
      }

      populateColors() {
        const product = this.selectedProduct();
        this.colorSelect.innerHTML = `<option value="">${this.escapeHtml(this.colorLabel)}</option>`;

        if (!product) {
          this.colorSelect.disabled = true;
          this.colorSelect.value = '';
          return;
        }

        const seen = new Set();
        product.variants.forEach((variant) => {
          const label = this.colorLabelFor(product, variant);
          if (seen.has(label)) return;
          seen.add(label);
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.dataset.price = String(variant.price);
          option.textContent = label;
          this.colorSelect.appendChild(option);
        });

        this.colorSelect.disabled = false;
        if (product.variants.length === 1) {
          this.colorSelect.value = String(product.variants[0].id);
        } else {
          this.colorSelect.value = '';
        }
      }

      formatMoney(cents) {
        const value = (Number(cents) / 100).toFixed(2);
        const format = this.dataset.moneyFormat || '${{amount}}';
        return format
          .replace(/\{\{\s*amount\s*\}\}/g, value)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
          .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, value.replace('.', "'"));
      }

      discountedPrice(cents) {
        if (!this.discountPercent) return Number(cents);
        const price = Number(cents);
        const discountAmount = Math.floor((price * this.discountPercent) / 100);
        return price - discountAmount;
      }

      hostVariantPrice() {
        if (!this.form) return 0;
        const id = Number(this.form.querySelector('[name="id"]')?.value);
        const match = this.hostVariants.find((variant) => Number(variant.id) === id);
        return match ? Number(match.price) : 0;
      }

      sync() {
        const checked = this.toggle.checked;
        this.card.classList.toggle('is-active', checked);
        this.badgeEl.textContent = checked ? this.offerBadge : this.offerBadgeIdle;

        if (!checked) {
          this.productSelect.value = '';
          this.populateColors();
        }

        this.renderPrices();

        if (this.errorEl) this.errorEl.hidden = !checked || Boolean(this.selectedVariant());
        this.scheduleAtcUpdate();
      }

      getItems() {
        const variant = this.selectedVariant();
        if (!this.toggle?.checked || !variant) return [];
        return [
          {
            id: Number(variant.id),
            quantity: 1,
            properties: { _bundle_save: 'true', _CrossSell: 'true' },
            price: variant.price,
          },
        ];
      }

      scheduleAtcUpdate() {
        clearTimeout(this.atcTimer);
        this.atcTimer = setTimeout(() => this.updateAtcLabel(), 20);
      }

      updateAtcLabel() {
        if (!this.form) return;
        if (document.querySelector('bundle-save') !== this) return;

        this.cacheAtcOriginals();

        const buttons = this.form.querySelectorAll('.product-form__submit.btn-temp, .product-form__submit[name="add"]');
        const items = Array.from(document.querySelectorAll('bundle-save')).flatMap((el) =>
          typeof el.getItems === 'function' ? el.getItems() : []
        );
        const defaultLabel = window.variantStrings?.addToCart || 'ADD TO CART';
        const extraTotal = items.reduce((sum, item) => sum + this.discountedPrice(item.price), 0);
        const count = 1 + items.length;

        buttons.forEach((button) => {
          if (button.classList.contains('no-add-to-cart-btn')) return;
          if (button.disabled) return;

          if (!items.length) {
            const span = button.querySelector('span');
            const original = this.atcOriginals.get(button);
            if (span && original) {
              span.innerHTML = original;
              return;
            }
            if (typeof setProductFormSubmitLabel === 'function') {
              setProductFormSubmitLabel(button, defaultLabel, { showCartIcon: true, productForm: this.form });
            } else if (span) {
              span.textContent = defaultLabel;
            }
            return;
          }

          const label = `${defaultLabel} (${count}) - ${this.formatMoney(this.hostVariantPrice() + extraTotal)}`;
          if (typeof setProductFormSubmitLabel === 'function') {
            setProductFormSubmitLabel(button, label, { showCartIcon: true, productForm: this.form });
          } else {
            const span = button.querySelector('span');
            if (span) span.textContent = label;
          }
        });

        this.updateRewardPoints(this.hostVariantPrice() + extraTotal);
      }

      updateRewardPoints(cents) {
        const points = String(Math.floor(Number(cents || 0) / 100));
        document.querySelectorAll('.product-reward [data-reward-points]').forEach((el) => {
          el.textContent = points;
        });
      }
    }
  );
}
